import {
  EmbedReportActions,
  EmitMessages,
  EmitTransport,
  ReportBuilderStore,
  RpcMessages,
} from '@mode/reports/sandbox/contract';
import { ReportActionEmitService } from '@mode/reports/sandbox/domain-viewer';
import { FeatureFlag, FeatureFlagsFacade, OrgTypes } from '@mode/shared/contract-common';
import { HelpDocsService, dispatchEvent } from '@mode/shared/util-js';
import { RpcTransport } from '@mode/shared/util-iframe';
import * as angular from 'angular';
import { format } from 'date-fns';
import { Observable, fromEvent, of } from 'rxjs';
import { concatMap, delay, distinctUntilKeyChanged, filter, first, skip, takeUntil } from 'rxjs/operators';
import { MixableBaseController } from '../../../common/component';
import { locationSearchLookup } from '../../../lib/location-search-lookup';
import { ReportLayoutHelpers } from '../../../lib/report-layout-helpers';
import { MSG_TYPES } from '@mode/shared/util-iframe';
import { EmbedReportSelectors } from '../domain/embed-report.selectors';
import * as $ from 'jquery';

export class EmbedViewerController extends MixableBaseController {
  static $inject = [
    '$window',
    '$scope',
    'EmitTransport',
    'RpcTransport',
    'EmbedReportActionEmitService',
    'RxStoreService',
    'FeatureFlagsFacade',
    'HelpDocsService',
  ];

  initialLayout: string;
  isCaptureMode: boolean;
  reportRunCreatedAt: string;

  fullWidthReport$: Observable<boolean>;
  isFilterPanelOpen$: Observable<boolean>;
  useMigratedFilterPanel$: Observable<boolean>;

  getRunCreatedAtGMTString: () => string;

  constructor(
    $window: angular.IWindowService,
    $scope: angular.IScope,
    emitter: EmitTransport,
    rpc: RpcTransport,
    actionEmitService: ReportActionEmitService, // Just so it is loaded
    rxStore: ReportBuilderStore,
    private featureFlagsFacade: FeatureFlagsFacade,
    private helpDocs: HelpDocsService
  ) {
    super();

    rxStore
      .select(EmbedReportSelectors.account)
      .pipe(distinctUntilKeyChanged('isThoughtspotIntegrated'), takeUntil(this.destroyed$))
      .subscribe((org: OrgTypes.MaybeIntegrated) => {
        helpDocs.setIsThoughtSpotIntegrated(org);
      });

    this.fullWidthReport$ = rxStore.select(EmbedReportSelectors.fullWidth);
    this.isFilterPanelOpen$ = rxStore.select(EmbedReportSelectors.isFilterPanelOpen);
    this.useMigratedFilterPanel$ = this.featureFlagsFacade.asObservable(FeatureFlag.ReportFiltersMigration);

    const planCode = locationSearchLookup($window.location.search, 'report_owner_plan') || 'standard';

    $($window.document.body).addClass('plan-' + planCode);

    this.isFilterPanelOpen$
      .pipe(skip(1), delay(1), takeUntil(this.destroyed$))
      .subscribe(() => dispatchEvent($window, 'resize'));

    $window.document.head.querySelector('#builtin-theme').addEventListener('load', () => {
      $scope.$evalAsync(() => {
        rxStore.dispatch(new EmbedReportActions.LoadThemeSuccess());
      });

      // the theme stuff can affect sizes of things, so good to resize afterwards
      // this generally only affects the builder as it can already have data loaded so the charts may render super fast
      $window.setTimeout(() => {
        dispatchEvent($window, 'resize');
      });
    });

    fromEvent($window.document, 'click').subscribe(() => {
      rxStore.dispatch(new EmbedReportActions.DocumentClick());
    });

    function getReportHeight() {
      const layout = $window.document.querySelector('[data-target="embed-layout"]');
      /**
       * Get the height from the bottom-most element. This is mainly done
       * to maintain exact same DOM structure as the builder. Otherwise we
       * would have just added another container element, which unfortunately
       * breaks some report builder drag/drop functionality.
       **/
      return Math.max(...Array.from(layout.children).map((child) => child.getBoundingClientRect().bottom));
    }

    rxStore
      .select(EmbedReportSelectors.reportRunCreatedAt)
      .pipe(
        filter((createdAt: Date | null) => !!createdAt),
        first()
      )
      .subscribe((createdAt) => {
        this.reportRunCreatedAt = format(createdAt, "MMM d, yyyy 'at' h:mm a 'UTC'");
      });

    // respond to mode.embed.js resize requests
    fromEvent<MessageEvent>($window, 'message')
      .pipe(filter((message) => message.data != null && message.data.type === MSG_TYPES.GetNewSize))
      .subscribe((message) => {
        setTimeout(function () {
          const height = getReportHeight();
          if (message.data.current.height !== height) {
            rxStore.dispatch(new EmbedReportActions.Autogrow(height + 1, $('html').width())); // + 1 to help avoid scrollbars
          }
        }, 500);
      });

    const initReport$ = rpc.getRpcRequestStream<RpcMessages.InitReport>($window, RpcMessages.MessageTypes.InitReport);

    initReport$.pipe(first()).subscribe((msg) => {
      ReportLayoutHelpers.applyTheme($window, msg.request.baseThemeHref, msg.request.customSource);
    });

    initReport$.subscribe((msg) => {
      this.isCaptureMode = msg.request.isCaptureMode;
      this.initialLayout = msg.request.layout;
      rxStore.dispatch(new EmbedReportActions.ResetReportFeatureState(this.initialLayout));
      this.featureFlagsFacade.init(msg.request.launchDarklyFlags);

      if (msg.request.embed_id != null) {
        $window.embed_id = msg.request.embed_id;
      }

      if ($window.document.documentElement.classList.contains('autogrow')) {
        // Allocate a good amount of time for tables to stabilize, and hopefully for any embedded JS to run
        of(2, 4, 7, 10, 15, 20, 25, 30, 35, 40)
          .pipe(concatMap((t) => of(t).pipe(delay(t * 1000))))
          .subscribe(() => {
            const height = getReportHeight();
            rxStore.dispatch(new EmbedReportActions.Autogrow(height + 1, $('html').width())); // + 1 to help avoid scrollbars
          });
      }

      rpc.sendResponse($window.parent, msg.requestId, new RpcMessages.EmptyResponse(true));
      $scope.$evalAsync();
    });

    // Receive update state messages and apply the patches
    rpc.getRpcRequestStream<RpcMessages.UpdateState>($window, RpcMessages.MessageTypes.UpdateState).subscribe((msg) => {
      $scope.$applyAsync(() => {
        rxStore.dispatch(new EmbedReportActions.UpdateReportFeatureState(msg.request.diff));
        rpc.sendResponse($window.parent, msg.requestId, new RpcMessages.EmptyResponse(true));
      });
    });

    emitter.emit(window.parent, new EmitMessages.Ready(window.location.href));

    // Expose rxStore.dispatch to embed for a la mode functionality.
    $window.dispatchAction = (action) => {
      rxStore.dispatch(action);
      $scope.$evalAsync();
    };
  }
}

export const EmbedViewerComponent: angular.IComponentOptions = {
  bindings: {},
  controller: EmbedViewerController,
  controllerAs: 'ctrl',
  template: `
  <div class="mode-embed-root" ng-class="{'cappie-adjustments': ctrl.isCaptureMode}">
      <div id="content-root" class="limit-viewport" ng-class="{'has-open-filter-panel': (ctrl.isFilterPanelOpen$ | async:this), 'full-width-report': (ctrl.fullWidthReport$ | async:this)}">
            <embed-layout
              data-target="embed-layout"
              initial-layout="ctrl.initialLayout"
              ng-if="ctrl.initialLayout"
              is-capture-mode="ctrl.isCaptureMode"
              class="mode-content"
            ></embed-layout>
            <filter-panel-container
              ng-if="(ctrl.useMigratedFilterPanel$ | async:this) == false"
              is-embed-viewer="true"
            ></filter-panel-container>
            <mode-report-viewer-filters-container
              ng-if="ctrl.useMigratedFilterPanel$ | async:this"
            ></mode-report-viewer-filters-container>
      </div>
      <div class="mode-capture-footer" ng-if="ctrl.isCaptureMode">
        Report run on {{ ctrl.reportRunCreatedAt }}
      </div>
    </div>
    `,
};
